import {
    AccessTime,
    Api,
    Assistant,
    Calculate,
    Diversity3,
    Email,
    FormatPaint,
    Info,
    LockPerson,
    Microsoft,
    PersonAdd,
    RecordVoiceOver,
    StarHalf,
    Surfing,
    Sync,
    TableChart,
} from '@mui/icons-material';
// import { setCompanyName } from '@store/reducer/companyReducer'; // Commented out - used in GeneralTab
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import type { AppState } from '@store/store';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './SettingsPage.css';
// import changeCompanyName from '@src/APIs/graphQl/Company/changeCompanyName'; // Commented out - used in GeneralTab
import getCompanySettings from '@src/APIs/graphQl/Company/getCompanySettings';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
// import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput'; // Commented out - used in GeneralTab
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import EditLevelList from '@src/App/Components/Settings/EditLevelList/EditLevelList';
import EditOuList from '@src/App/Components/Settings/EditOuList/EditOuList';
import EditPermissionGroupsList from '@src/App/Components/Settings/EditPermissionGroupsList/EditPermissionGroupsList';
import StyleSettings from '@src/App/Components/Settings/StyleSettings/StyleSettings';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Company from '@src/Objects/Company';

import { CalculationsTab } from './CalculationsTab';
import EmailSettingsPage from './EmailSettingsPage';
import { EmployeeTalkTemplateTab } from './employeeTalkTemplateTab';
import { GeneralTab } from './GeneralTab';
import { HolidayTab } from './HolidayTab';
import { MicrosoftSettingsTab } from './MicrosoftSettingsTab';
import { RegistrationTab } from './RegistrationTab';
import RestApiTab from './RestApiTab';
import SlackSettingsTab from './SlackSettingsTab';
import { SuggestionsTab } from './SuggestionsTab';
import { TimeSyncTab } from './TimeSyncTab';

interface SettingsPageProps {
    tabId: ESettingsTabs;
}

/**
 * Function component for the SettingsPage.
 * @param {SettingsPageProps} tab - The tab parameter.
 * @returns {JSX.Element} The SettingsPage component.
 */
function SettingsPage({ tabId }: SettingsPageProps) {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    // const dispatch = useDispatch(); // Commented out - used in GeneralTab
    const companyId = useSelector((state: AppState) => state.company.companyId);
    const companyName = useSelector((state: AppState) => state.company.name);
    const width = useSelector((state: AppState) => state.windowSize.width);
    const offline = useSelector((state: AppState) => state.client.offline);
    const scrollTabs = width < 700;
    const wrappedTab = width < 900;
    const [activeRole, setActiveRole] = useState('admin');

    const [company, setCompany] = React.useState<Company | null>(null);
    // const [editName, setEditName] = React.useState<string>(""); // Commented out - used in GeneralTab
    // const [saving, setSaving] = React.useState<boolean>(false); // Commented out - used in GeneralTab
    const loadCompanySettings = useCallback(
        () =>
            getCompanySettings(companyId, (companySettings) => {
                setCompany(companySettings);
            }),
        [companyId],
    );

    React.useEffect(() => {
        loadCompanySettings();
    }, [loadCompanySettings]);

    if (offline)
        return (
            <InnerLayout title={<div className="settingsTitle">{companyName}</div>}>
                <div className="companySettings">{langStrings.offline}</div>
            </InnerLayout>
        );

    if (!company) return <LoadingBox />;

    const mainTabs = [
        {
            icon: <Info />,
            label: langStrings.general,
            key: 0,
            tabs: [
                {
                    url: `/${ELinks.SETTINGS}`,
                    icon: <Info />,
                    label: langStrings.general,
                    key: ESettingsTabs.GENERAL,
                    render: <GeneralTab company={company} setCompany={setCompany} />,
                },
                {
                    url: `/${ELinks.SETTINGS_STYLE}`,
                    icon: <FormatPaint />,
                    label: langStrings.designTitle,
                    key: ESettingsTabs.STYLE,
                    render: tabId === ESettingsTabs.STYLE && <StyleSettings />,
                },
                {
                    url: `/${ELinks.SETTINGS_LEVELS}`,
                    icon: <StarHalf />,
                    label: langStrings.levelDefinitions,
                    key: ESettingsTabs.LEVELS,
                    render: company.level.length && <EditLevelList level={company.level} setCompany={setCompany} />,
                },
            ],
        },
        {
            icon: <Diversity3 />,
            label: langStrings.userAndOUs,
            key: 1,
            tabs: [
                {
                    url: `/${ELinks.SETTINGS_ORGUNITS}`,
                    icon: <Diversity3 />,
                    label: langStrings.orgUnits,
                    key: ESettingsTabs.ORGUNITS,
                    render: (
                        <EditOuList
                            employees={company.employees}
                            OUs={company.OUs}
                            permissionGroups={company.permissionGroups}
                            setCompany={setCompany}
                        />
                    ),
                },
                {
                    url: `/${ELinks.SETTINGS_REGISTRATION}`,
                    icon: <PersonAdd />,
                    label: langStrings.registration,
                    key: ESettingsTabs.REGISTRATION,
                    render: <RegistrationTab setCompany={setCompany} company={company} />,
                },
                {
                    url: `/${ELinks.SETTINGS_PERMISSIONS}`,
                    icon: <LockPerson />,
                    label: langStrings.permissions,
                    key: ESettingsTabs.PERMISSIONS,
                    render: (
                        <EditPermissionGroupsList
                            permissionGroups={company.permissionGroups}
                            employees={company.employees}
                            setCompany={setCompany}
                        />
                    ),
                },
            ],
        },
        {
            icon: <Sync />,
            label: langStrings.synchronisation,
            key: 2,
            tabs: [
                {
                    url: `/${ELinks.SETTINGS_MICROSOFT}`,
                    icon: <Microsoft />,
                    label: langStrings.microsoft,
                    key: ESettingsTabs.MICROSOFT,
                    render: (
                        <MicrosoftSettingsTab company={company} setCompany={setCompany} reload={loadCompanySettings} />
                    ),
                },
                {
                    url: `/${ELinks.SETTINGS_TIME_SYNC}`,
                    icon: <AccessTime />,
                    label: langStrings.timeSync,
                    key: ESettingsTabs.TIME_SYNC,
                    render: (
                        <TimeSyncTab
                            clockdoEmail={company.clockodoSync.email}
                            timeSync={company.timeSync}
                            clockodoStatis={company.clockodoStats}
                            zepStatis={company.zepStats}
                            zepInstance={company.zepInstance}
                            setCompany={setCompany}
                            reload={loadCompanySettings}
                        />
                    ),
                },
                {
                    url: `/${ELinks.SETTINGS_HOLIDAYS}`,
                    icon: <Surfing />,
                    label: langStrings.holidays,
                    key: ESettingsTabs.HOLIDAYS,
                    render: (
                        <HolidayTab
                            timeSync={company.timeSync}
                            holidaySettings={company.holidaySettings}
                            setHolidaySettings={(newHolidaySettings) => {
                                setCompany((prevCompany) => {
                                    if (prevCompany) {
                                        return { ...prevCompany, holidaySettings: newHolidaySettings };
                                    }
                                    return null;
                                });
                            }}
                        />
                    ),
                },
                {
                    url: `/${ELinks.SETTINGS_RESTAPI}`,
                    icon: <Api />,
                    label: langStrings.restAPI,
                    key: ESettingsTabs.RESTAPI,
                    render: <RestApiTab apiKeys={company.apiKeys} setCompany={setCompany} />,
                },
                {
                    url: `/${ELinks.SETTINGS_SLACK}`,
                    icon: (
                        <img
                            alt="Slack"
                            src="/icons/slack-logo_outline.png"
                            height={20}
                            style={{ filter: 'invert(0.4)' }}
                        />
                    ),
                    label: langStrings.slack,
                    key: ESettingsTabs.SLACK,
                    render: <SlackSettingsTab slack={company.slack} setCompany={setCompany} />,
                },
            ],
        },
        {
            icon: <TableChart />,
            label: langStrings.dataTabTitle,
            key: 3,
            tabs: [
                {
                    url: `/${ELinks.SETTINGS_CALCULATIONS}`,
                    icon: <Calculate />,
                    label: langStrings.calculations,
                    key: ESettingsTabs.CALCULATIONS,
                    render: <CalculationsTab calculations={company.calculations} setCompany={setCompany} />,
                },
                {
                    url: `/${ELinks.SETTINGS_SUGGESTIONS}`,
                    icon: <Assistant />,
                    label: langStrings.suggestions,
                    key: ESettingsTabs.SUGGESTIONS,
                    render: <SuggestionsTab />,
                },
                //Email Settings
                {
                    url: `/${ELinks.SETTINGS_EMAIL}`,
                    icon: <Email />,
                    label: langStrings.emailSettings,
                    key: ESettingsTabs.EMAIL,
                    render: (
                        <EmailSettingsPage
                            emailSettings={company.emailSettings}
                            microsoftConnectionStatus={company.microsoftSettings.connectionState}
                            globalEmailSupport={company.globalEmailSupport}
                            setCompany={setCompany}
                        />
                    ),
                },
                //EmployeeTalkTemplate
                {
                    url: `/${ELinks.SETTINGS_EMPLOYEE_TALK_TEMPLATE}`,
                    icon: <RecordVoiceOver />,
                    label: langStrings.employeeTalkTemplate,
                    key: ESettingsTabs.EMPLOYEE_TALK_TEMPLATE,
                    render: <EmployeeTalkTemplateTab />,
                },
            ],
        },
    ];

    const activeMainTab = mainTabs.find((mt) => mt.tabs.some((st) => st.key === tabId));
    const activeMainTabId = activeMainTab ? activeMainTab.key : mainTabs[0].key;
    const activeSubTab = mainTabs
        .find((mt) => mt.tabs.some((st) => st.key === tabId))
        ?.tabs.find((st) => st.key === tabId);

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: getAppIcon(EIconCategory.DASHBOARD),
            onClick: () => navigate(`/${ELinks.DASHBOARD}`),
            variant: 'primary',
        },
        {
            id: 'employees',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Einstellungen"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <InnerLayout
                title={
                    <Box className="settingsTitle">
                        <Typography variant="h5">{companyName}</Typography>
                        <Typography
                            sx={{
                                fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif !important',
                                fontWeight: 400,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                lineHeight: 1.4,
                                color: 'text.secondary',
                                mt: 0.5,
                                opacity: 0.8,
                            }}
                        >
                            {langStrings.oftenUsed.settings}
                        </Typography>
                        {/* Company Name Edit - Commented out, use GeneralTab instead */}
                        {/*
                {
                    editName === "" ? companyName : <TextInput
                        value={editName}
                        onChange={function (val: string): void
                        {
                            setEditName(val);
                        }}
                    />
                }
                <div className="editTitle">
                    <div className="editTitleIcon"
                        style={{ display: saving || company.name === '' ? 'none' : 'inherit' }}
                        onClick={() =>
                        {
                            if (editName === "")
                            {
                                setEditName(company.name)
                            } else
                            {
                                if (company.name !== editName)
                                    setSaving(true);
                                changeCompanyName(editName)
                                    .then((result) =>
                                    {
                                        setCompany(result);
                                        dispatch(setCompanyName(result.name))
                                        setEditName("");
                                        setSaving(false);
                                    })
                                    .catch(() =>
                                    {
                                        alert(langStrings.errorChangeName);
                                        setTimeout(() =>
                                        {
                                            setSaving(false);
                                        }, 3000);
                                    })
                            }
                        }}>
                        {editName === "" ? <Edit /> : <Check />}
                    </div>
                </div>
                */}
                    </Box>
                }
            >
                <Box className="companySettings">
                    <Tabs
                        className="tabControl"
                        value={activeMainTabId}
                        // onChange={() => { }}
                        variant={scrollTabs ? 'scrollable' : 'fullWidth'}
                        scrollButtons="auto"
                        centered={!scrollTabs}
                        textColor="primary"
                        indicatorColor="primary"
                        style={{ borderBottom: '1px solid #ccc' }}
                    >
                        {mainTabs.map((tab) => (
                            <Tab
                                value={tab.key}
                                key={tab.key}
                                wrapped={wrappedTab ? true : undefined}
                                onClick={() => navigate(tab.tabs[0].url)}
                                icon={tab.icon}
                                label={tab.label}
                            />
                        ))}
                    </Tabs>

                    <Tabs
                        className="tabControl"
                        value={tabId}
                        // onChange={() => { }}
                        textColor="secondary"
                        indicatorColor="secondary"
                        variant={'fullWidth'}
                        scrollButtons="auto"
                        style={{ borderBottom: '1px solid #ccc' }}
                        centered={true}
                    >
                        {activeMainTab?.tabs.map((tab) => (
                            <Tab
                                key={tab.key}
                                value={tab.key}
                                color="secondary"
                                sx={{ fontSize: '0.7rem', minWidth: '50px', padding: '6px' }}
                                wrapped={wrappedTab ? true : undefined}
                                onClick={() => navigate(tab.url)}
                                icon={tab.icon}
                                label={tab.label}
                            />
                        ))}
                    </Tabs>
                    {activeSubTab ? activeSubTab.render : <LoadingBox />}
                </Box>
            </InnerLayout>
        </>
    );
}

export default SettingsPage;

export enum ESettingsTabs {
    GENERAL = 0,
    STYLE = 1,
    LEVELS = 2,
    REGISTRATION = 3,
    ORGUNITS = 4,
    PERMISSIONS = 5,
    CALCULATIONS = 6,
    // SYNCHRONISATION = 7,
    TIME_SYNC = 8,
    HOLIDAYS = 9,
    EMAIL = 10,
    RESTAPI = 11,
    MICROSOFT = 12,
    SUGGESTIONS = 13,
    SLACK = 14,
    EMPLOYEE_TALK_TEMPLATE = 15,
}
